import { useState, useRef, useEffect } from "react";
import React from "react";

import Select from "react-select";
import { universalSelectStyles } from "../SelectStyle";
import ColorMapper from "../CharacterCreator/ColorMapper";
import SwatchPicker from "../SwatchPicker";

import styles from "./EquipmentSlot.module.css";

export interface SlotOption {
    value: number;
    label: string;
}

interface Props {
    editing?: boolean;
    value: number | undefined | null;
    options: SlotOption[];
    icon?: string;
    backLabel?: string;
    primaryColor?: string;
    secondaryColor?: string;
    canColor?: boolean;
    canClear?: boolean;
    bodyType?: number;
    onBodyTypeChange?: (bodyType: number) => void;
    onPrimaryColorChange?: (newColor: string) => void;
    onSecondaryColorChange?: (newColor: string) => void;
    onValueChange?: (newValue: any) => void;
}

export default function EquipmentSlot({
    editing,
    value,
    options,
    icon,
    backLabel,
    primaryColor,
    secondaryColor,
    canColor,
    canClear,
    bodyType,
    onBodyTypeChange,
    onPrimaryColorChange,
    onSecondaryColorChange,
    onValueChange,
}: Props) {
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        setInputValue("");
    }, [value]);

    return (
        <div className={styles.equipmentSlot}>
            {backLabel && <div className={styles.backText}>{backLabel}</div>}
            {icon && <img src={icon} width="64" height="64" />}
            <div className={styles.slotOptions}>
                <div className={styles.colorRow}>
                    {bodyType && (
                        <div className={styles.genderToggle}>
                            <div
                                className={`${styles.genderOption} ${bodyType == 1 ? styles.active : ""}`}
                                onClick={() => {
                                    if (onBodyTypeChange) onBodyTypeChange(1);
                                }}
                            >
                                ◆
                            </div>
                            <div
                                className={`${styles.genderOption} ${bodyType == 2 ? styles.active : ""}`}
                                onClick={() => {
                                    if (onBodyTypeChange) onBodyTypeChange(2);
                                }}
                            >
                                ⬤
                            </div>
                        </div>
                    )}
                    {editing && (
                        <Select
                            styles={universalSelectStyles}
                            options={options}
                            inputValue={inputValue}
                            onChange={onValueChange}
                            menuPortalTarget={document.body}
                            onInputChange={(newValue) =>
                                setInputValue(newValue as string)
                            }
                            onMenuOpen={() => {}}
                            onMenuClose={() => {}}
                            value={
                                options.find((o) => o.value === value) || null
                            }
                            isSearchable={true}
                            isClearable={canClear === true}
                        />
                    )}
                    {!editing && (
                        <div className={styles.itemText}>
                            {options.find((o) => o.value === value)?.label}
                        </div>
                    )}
                </div>
                <div className={styles.colorRow}>
                    <SwatchPicker
                        color={primaryColor ?? ""}
                        onChange={(color) => {
                            if (onPrimaryColorChange)
                                onPrimaryColorChange(color);
                        }}
                        style={{
                            borderRadius: "9999px",
                            width: "16px",
                            height: "16px",
                            padding: 0,
                            opacity: canColor ? 1 : 0,
                        }}
                        readOnly={!editing}
                    />
                    {editing && (
                        <input
                            type="text"
                            value={primaryColor?.replace("#", "")}
                            onChange={(ev) => {
                                if (onPrimaryColorChange)
                                    onPrimaryColorChange(
                                        `#${ev.target.value.replace("#", "")}`,
                                    );
                            }}
                            style={{ flex: 1, opacity: canColor ? 1 : 0 }}
                        />
                    )}
                    {!editing && (
                        <div
                            className={styles.colorText}
                            style={{
                                opacity: canColor ? 1 : 0,
                            }}
                        >
                            {primaryColor}
                        </div>
                    )}
                    <SwatchPicker
                        color={secondaryColor ?? ""}
                        onChange={(color) => {
                            if (!onSecondaryColorChange) return;
                            onSecondaryColorChange(color);
                        }}
                        style={{
                            borderRadius: "9999px",
                            width: "16px",
                            height: "16px",
                            padding: 0,
                            opacity: canColor ? 1 : 0,
                        }}
                        readOnly={!editing}
                    />
                    {editing && (
                        <input
                            type="text"
                            value={secondaryColor?.replace("#", "")}
                            onChange={(ev) => {
                                if (onSecondaryColorChange)
                                    onSecondaryColorChange(
                                        `#${ev.target.value.replace("#", "")}`,
                                    );
                            }}
                            style={{ flex: 1, opacity: canColor ? 1 : 0 }}
                        />
                    )}
                    {!editing && (
                        <div
                            style={{
                                opacity: canColor ? 1 : 0,
                            }}
                            className={styles.colorText}
                        >
                            {secondaryColor}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
